import React, { useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import {
  Box,
  Card,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Image } from '@realadvisor/image';
import { Building2Icon, EyeIcon, EyeOffIcon, MapPinIcon } from 'lucide-react';

import type {
  DeleteCmaAgentTransactionMutationVariables,
  InsertCmaAgentTransactionMutationVariables,
} from '@graphql/__generated__/graphql';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useReportContext } from '@modules/_shared/components/report-editor/ReportEditorContext';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import {
  formatDistance,
  toHumanReadableDate,
} from '@modules/_shared/utils/formatting';
import {
  DELETE_CMA_AGENT_TRANSACTION,
  GET_CMA_AGENT_TRANSACTIONS,
  INSERT_CMA_AGENT_TRANSACTION,
} from '@modules/reports/cma-reports/cmaReportsQueries';
import type { AgentTransaction } from '@modules/reports/cma-reports/step-agent/types';
import type { CMAPageId } from '@modules/reports/shared/utils';
import { useLocale } from '@shared/hooks/locale';
import type { Translate } from '@shared/hooks/locale';
import { getCurrencyByCountryCode } from '@shared/LocaleContext';
import { formatPrice } from '@shared/utils/format-price';

export type AgentTransactionCardProps = {
  transaction: AgentTransaction;
};

const formatPropertyInfo = (
  rooms: number | null | undefined,
  bedrooms: number | null | undefined,
  livingSurface: number | null | undefined,
  builtSurface: number | null | undefined,
  isTenantSpanish: boolean,
  t: Translate,
): string => {
  const displayRooms = isTenantSpanish ? bedrooms : rooms;
  const displaySurface = isTenantSpanish ? builtSurface : livingSurface;

  if (displayRooms && displaySurface) {
    const roomLabel = `${displayRooms} ${t('Rooms')}`;
    return `${roomLabel} • ${displaySurface} m²`;
  }

  if (displayRooms) {
    return `${displayRooms} ${t('Rooms')}`;
  }

  if (displaySurface) {
    return `${displaySurface} m²`;
  }
  return '—';
};

export const AgentTransactionCard = ({
  transaction,
}: AgentTransactionCardProps) => {
  const { t, locale } = useLocale();
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const { data: cmaReport } = useReportContext<CMAPageId>();
  const { me } = useAppData();

  const isTenantSpanish = me?.tenant.country_code === 'ES';

  const isVisible = transaction.is_visible;

  const [deleteTransaction, { loading: deleteLoading }] = useMutation(
    DELETE_CMA_AGENT_TRANSACTION,
    {
      onError: setErrorLike,
      refetchQueries: [GET_CMA_AGENT_TRANSACTIONS],
      awaitRefetchQueries: true,
    },
  );

  const [insertTransaction, { loading: insertLoading }] = useMutation(
    INSERT_CMA_AGENT_TRANSACTION,
    {
      onError: setErrorLike,
      refetchQueries: [GET_CMA_AGENT_TRANSACTIONS],
      awaitRefetchQueries: true,
    },
  );

  const isLoading = deleteLoading || insertLoading;

  const transactionData = transaction.transaction;
  const property = transactionData.property ?? {};
  const distanceMeters = transaction.distance_in_meters;
  const imageUrl = property?.property_images?.[0]?.image?.url;

  const currency = getCurrencyByCountryCode(property?.country_code ?? 'CH');

  const pricePerSqm = useMemo(() => {
    const surface = isTenantSpanish
      ? property?.built_surface
      : property?.living_surface;

    if (surface == null) {
      return null;
    }

    return Math.round((transactionData.purchase_price ?? 0) / surface);
  }, [
    isTenantSpanish,
    property?.built_surface,
    property?.living_surface,
    transactionData.purchase_price,
  ]);

  const handleVisibilityToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();

    const variables:
      | DeleteCmaAgentTransactionMutationVariables
      | InsertCmaAgentTransactionMutationVariables = {
      cma_report_id: cmaReport?.id ?? '',
      transaction_id: transaction.transaction_id,
    };

    if (!isVisible) {
      (
        variables as InsertCmaAgentTransactionMutationVariables
      ).distance_in_meters = distanceMeters;
      (variables as InsertCmaAgentTransactionMutationVariables).zone =
        transaction.zone;
    }

    if (isVisible) {
      await deleteTransaction({
        variables: variables as DeleteCmaAgentTransactionMutationVariables,
      });
    } else {
      await insertTransaction({
        variables: variables as InsertCmaAgentTransactionMutationVariables,
      });
    }
  };

  return (
    <Card
      elevation={0}
      sx={{
        transition: 'all 0.2s ease',
        opacity: isVisible ? 1 : 0.5,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack direction="row">
        {imageUrl ? (
          <Image
            key={imageUrl}
            src={imageUrl}
            objectFit="cover"
            loading="lazy"
            options={{ f: 'jpg' }}
            css={{
              width: 160,
              '&': {
                height: 'auto',
                display: 'flex',
                flexDirection: 'column',
              },
              img: {
                height: 0,
                flexGrow: 1,
              },
              '@media (max-width: 600px)': {
                width: 128,
              },
            }}
          />
        ) : (
          <Box
            sx={{
              width: { xs: 124, sm: 160 },
              maxWidth: '100%',
              height: 'auto',
              aspectRatio: '1 / 1',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'action.hover',
            }}
          >
            <Building2Icon size={64} color="var(--mui-palette-grey-400)" />
          </Box>
        )}

        <Stack
          sx={{
            flex: 1,
            pr: 1,
            pl: 2,
            py: 1.5,
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {formatPropertyInfo(
                property?.number_of_rooms,
                property?.number_of_bedrooms,
                property?.living_surface,
                property?.built_surface,
                isTenantSpanish,
                t,
              )}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  mr: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: { xs: '150px', sm: '200px' },
                }}
              >
                {property?.locality}
                {property?.postcode && ` (${property?.postcode})`}
              </Typography>

              {distanceMeters != null && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.25,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    •
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.25,
                      flexShrink: 0,
                    }}
                  >
                    <MapPinIcon
                      size={'16px'}
                      color={'var(--mui-palette-text-secondary)'}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {formatDistance(distanceMeters)}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            {transactionData.signature_date && (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t('Sold on')}{' '}
                {toHumanReadableDate(locale, transactionData.signature_date)}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              mt: 1,
              gap: 1,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: '1.25rem',
                }}
              >
                {formatPrice(transactionData.purchase_price, locale, currency)}
              </Typography>
              {pricePerSqm && (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                  }}
                >
                  {formatPrice(pricePerSqm, locale, currency)} / m²
                </Typography>
              )}
            </Box>

            <IconButton
              onClick={handleVisibilityToggle}
              size="medium"
              disabled={isLoading}
              sx={{ ml: 'auto', color: 'text.secondary' }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : isVisible ? (
                <EyeIcon size={20} />
              ) : (
                <EyeOffIcon size={20} />
              )}
            </IconButton>
          </Box>
        </Stack>
      </Stack>
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </Card>
  );
};
